/*
 * TreeViewTypes  types
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import { IButtonPropsBase } from "../Button/Button";
import { IDropdownMenuItem } from "../DropdownMenu";

export enum TREE_VIEW_STYLE {
	DEFAULT = "default",
	MONOSPACE = "monospace"
}

export enum TREE_NODE_ACTION_STYLE {
	DEFAULT = "default",
	PRIMARY = "primary",
	GHOST = "ghost"
}

export interface ITreeState {
	nodes: {
		[K in string]: {
			expanded?: boolean;
		};
	};
}

export interface ITreeNodeFlag {
	id?: string;
	icon: string;
	tooltip?: string;
	className?: string;
	onClick?: () => void;
}

export interface ITreeNodeAction extends Omit<IButtonPropsBase, "icon" | "label"> {
	id?: string;
	icon: string;
	hasPrimaryAction?: boolean;
	style?: TREE_NODE_ACTION_STYLE;
	className?: string;
	onClick?: TTreeViewNodeActionClickHandler;
}

export interface ITreeNode {
	id: string | number;
	icon?: string;
	label: string;
	tooltip?: string;
	value?: string | number | boolean | null;
	description?: string;
	isSelected?: boolean;
	selectable?: boolean;
	disabled?: boolean;
	isLoadable?: boolean;
	isLeaf?: boolean;
	className?: string;
	defaultExpanded?: boolean;
	flags?: ITreeNodeFlag[];
	actions?: ITreeNodeAction[];
	children?: ITreeNode[];
	metaData?: Record<string, unknown>;
}

export type TTreeViewNodeSelectHandler = (
	node: ITreeNode,
	{ selected, isMultiselect }: { selected: boolean; isMultiselect: boolean }
) => void;

export type TTreeViewNodeHoverHandler = (node: ITreeNode, { hovered }: { hovered: boolean }) => void;

export type TTreeViewNodeDragStartHandler = (event: {
	draggedNode: ITreeNode;
	event: React.DragEvent<HTMLDivElement>;
}) => void;

export type TTreeViewNodeDragDropHandler = (event: {
	draggedNode: ITreeNode;
	draggedNodePositionPath: number[];
	dropTargetNode: ITreeNode;
	dropTargetParentNode: ITreeNode;
	dropPosition: number;
	dropPositionPath: number[];
	event: React.MouseEvent<HTMLDivElement>;
}) => void;

export type TTreeViewNodeClickHandler = (node: ITreeNode, event: React.MouseEvent<HTMLDivElement>) => void;

export type TTreeViewNodeFlagClickHandler = (node: ITreeNode, flag: ITreeNodeFlag) => void;

export type TTreeViewNodeActionClickHandler = (node: ITreeNode, action: ITreeNodeAction) => void;

export type TTreeViewNodeActionMenuItemClickHandler = (
	node: ITreeNode,
	action: ITreeNodeAction,
	menuItem: IDropdownMenuItem
) => void;

export type ITreeViewAllowDrop = (info: {
	draggedNode: ITreeNode | null;
	dropTargetNode: ITreeNode;
	dropPosition: number;
}) => boolean;
